import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@clerk/react';
import { Loader2, RefreshCw } from 'lucide-react';
import { useToast } from '@/shared/contexts/ToastContext';
import { accountModalAuthButtonClass } from './accountStyles';
import type {
  AdminAccountDetail,
  AdminAccountSummaryRow
} from './api/accountApi';
import {
  adminFetchAccountDetail,
  adminFetchAccountsPage,
  adminPatchPricing,
  adminPostLedger,
  adminPutTerritory
} from './api/accountApi';

const AccountElAccountsPage = () => {
  const { getToken } = useAuth();
  const { notify } = useToast();
  const getTok = useCallback(() => getToken(), [getToken]);

  const [rows, setRows] = useState<AdminAccountSummaryRow[]>([]);
  const [search, setSearch] = useState('');
  const [cursor, setCursor] = useState<string | null>(null);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<AdminAccountDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const [buyerLead, setBuyerLead] = useState('');
  const [rentalLead, setRentalLead] = useState('');
  const [ledgerDir, setLedgerDir] = useState<'credit' | 'debit'>('credit');
  const [ledgerAmt, setLedgerAmt] = useState('');
  const [ledgerMemo, setLedgerMemo] = useState('');
  const [lat, setLat] = useState('');
  const [lng, setLng] = useState('');
  const [radiusM, setRadiusM] = useState('');
  const [territoryFsas, setTerritoryFsas] = useState('');

  const loadFirstPage = useCallback(async () => {
    setLoadingList(true);
    try {
      const page = await adminFetchAccountsPage(getTok, 50, null);
      setRows(page.accounts);
      setCursor(page.nextCursor);
    } catch (e) {
      notify(
        'error',
        e instanceof Error ? e.message : 'Could not load accounts'
      );
      setRows([]);
      setCursor(null);
    } finally {
      setLoadingList(false);
    }
  }, [getTok, notify]);

  useEffect(() => {
    void loadFirstPage();
  }, [loadFirstPage]);

  const loadMore = async () => {
    if (!cursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const page = await adminFetchAccountsPage(getTok, 50, cursor);
      setRows(prev => [...prev, ...page.accounts]);
      setCursor(page.nextCursor);
    } catch (e) {
      notify('error', e instanceof Error ? e.message : 'Could not load more');
    } finally {
      setLoadingMore(false);
    }
  };

  const openDetail = async (clerkUserId: string) => {
    setSelectedId(clerkUserId);
    setLoadingDetail(true);
    setDetail(null);
    try {
      const d = await adminFetchAccountDetail(getTok, clerkUserId);
      setDetail(d);
      setBuyerLead(String(d.buyerLead));
      setRentalLead(String(d.rentalLead));
      if (d.territory) {
        setLat(String(d.territory.centerLat));
        setLng(String(d.territory.centerLng));
        setRadiusM(String(d.territory.radiusM));
        setTerritoryFsas((d.territory.postalFsaPrefixes ?? []).join(', '));
      } else {
        setLat('43.65');
        setLng('-79.38');
        setRadiusM('120000');
        setTerritoryFsas('');
      }
    } catch (e) {
      notify(
        'error',
        e instanceof Error ? e.message : 'Could not load account'
      );
    } finally {
      setLoadingDetail(false);
    }
  };

  const savePricing = async () => {
    if (!selectedId) return;
    const b = Number(buyerLead);
    const r = Number(rentalLead);
    if (!Number.isFinite(b) || !Number.isFinite(r) || b <= 0 || r <= 0) {
      notify('error', 'Enter positive numbers for buyer and rental pricing.');
      return;
    }
    try {
      await adminPatchPricing(getTok, selectedId, {
        buyerLead: b,
        rentalLead: r
      });
      notify('success', 'Pricing updated.');
      void loadFirstPage();
      void openDetail(selectedId);
    } catch (e) {
      notify('error', e instanceof Error ? e.message : 'Update failed');
    }
  };

  const submitLedger = async () => {
    if (!selectedId) return;
    const amt = Number(ledgerAmt);
    if (!Number.isFinite(amt) || amt <= 0) {
      notify('error', 'Enter a positive amount.');
      return;
    }
    try {
      const { balance } = await adminPostLedger(getTok, selectedId, {
        direction: ledgerDir,
        amount: amt,
        memo: ledgerMemo.trim() || '(no memo)'
      });
      notify('success', `Ledger updated. Balance: $${balance.toFixed(2)}`);
      setLedgerAmt('');
      setLedgerMemo('');
      void loadFirstPage();
      void openDetail(selectedId);
    } catch (e) {
      notify('error', e instanceof Error ? e.message : 'Ledger failed');
    }
  };

  const saveTerritory = async () => {
    if (!selectedId) return;
    const centerLat = Number(lat);
    const centerLng = Number(lng);
    const rm = Number(radiusM);
    if (
      !Number.isFinite(centerLat) ||
      !Number.isFinite(centerLng) ||
      !Number.isFinite(rm)
    ) {
      notify('error', 'Territory fields must be numbers.');
      return;
    }
    if (rm < 500 || rm > 200_000) {
      notify('error', 'radiusM must be between 500 and 200000.');
      return;
    }
    try {
      await adminPutTerritory(getTok, selectedId, {
        centerLat,
        centerLng,
        radiusM: rm,
        postalFsaPrefixes: territoryFsas
          .split(',')
          .map(x => x.trim().toUpperCase())
          .filter(Boolean)
      });
      notify('success', 'Territory saved.');
      void loadFirstPage();
      void openDetail(selectedId);
    } catch (e) {
      notify('error', e instanceof Error ? e.message : 'Territory save failed');
    }
  };

  const filteredRows = rows.filter(r => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    const haystack = `${r.name ?? ''} ${r.email ?? ''}`.toLowerCase();
    return haystack.includes(q);
  });

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-slate-900">EL Accounts</h1>
          <p className="mt-1 text-sm text-slate-600">
            Accounts, pricing, wallet adjustments, and territory.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void loadFirstPage()}
          disabled={loadingList}
          className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-800 shadow-sm hover:bg-slate-50 disabled:opacity-50"
        >
          {loadingList ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          ) : (
            <RefreshCw className="h-4 w-4" aria-hidden />
          )}
          Refresh list
        </button>
      </div>

      <div className="rounded-xl border border-slate-200/80 bg-white p-4 shadow-sm">
        <div className="mb-3 flex items-center justify-end">
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by name or email..."
            className="w-full max-w-xs rounded-md border border-slate-200 px-2 py-1.5 text-sm text-slate-900"
          />
        </div>
        {loadingList ? (
          <div className="flex justify-center py-12 text-slate-500">
            <Loader2 className="h-8 w-8 animate-spin" aria-hidden />
          </div>
        ) : (
          <div className="overflow-auto rounded-xl border border-slate-200 bg-white">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-600">
                <tr>
                  <th className="px-3 py-2">Name</th>
                  <th className="px-3 py-2">Email</th>
                  <th className="px-3 py-2">Clerk user ID</th>
                  <th className="px-3 py-2">Balance</th>
                  <th className="px-3 py-2">Pricing</th>
                  <th className="px-3 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map(r => (
                  <tr
                    key={r.clerkUserId}
                    className="border-t border-slate-100 align-top"
                  >
                    <td className="px-3 py-3 text-slate-800">
                      {r.name || '—'}
                    </td>
                    <td className="px-3 py-3 text-slate-700">
                      {r.email || '—'}
                    </td>
                    <td className="px-3 py-3 font-mono text-xs text-slate-600">
                      {r.clerkUserId}
                    </td>
                    <td className="px-3 py-3 text-slate-700">
                      ${r.balanceCached.toFixed(2)}
                    </td>
                    <td className="px-3 py-3 text-slate-700">
                      buyer ${r.buyerLead} · rental ${r.rentalLead}
                    </td>
                    <td className="px-3 py-3">
                      <button
                        type="button"
                        onClick={() => void openDetail(r.clerkUserId)}
                        className={`rounded-md border px-3 py-1.5 text-xs font-medium ${
                          selectedId === r.clerkUserId
                            ? 'border-[#2b6cff] bg-[#eef4ff] text-[#2b6cff]'
                            : 'border-slate-200 text-slate-700 hover:bg-slate-50'
                        }`}
                      >
                        {selectedId === r.clerkUserId ? 'Selected' : 'Manage'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {cursor ? (
          <button
            type="button"
            onClick={() => void loadMore()}
            disabled={loadingMore}
            className="mt-3 w-full rounded-lg border border-slate-200 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            {loadingMore ? 'Loading…' : 'Load more'}
          </button>
        ) : null}
      </div>

      <div className="rounded-xl border border-slate-200/80 bg-white p-4 shadow-sm">
        {!selectedId ? (
          <p className="py-12 text-center text-sm text-slate-500">
            Select an account to edit.
          </p>
        ) : loadingDetail ? (
          <div className="flex justify-center py-16 text-[#2b6cff]">
            <Loader2 className="h-8 w-8 animate-spin" aria-hidden />
          </div>
        ) : detail ? (
          <div className="space-y-8">
            <div>
              <h2 className="text-sm font-semibold text-slate-800">Account</h2>
              <p className="mt-1 font-mono text-xs text-slate-700">
                {detail.clerkUserId}
              </p>
              <p className="mt-2 text-sm text-slate-600">
                Balance{' '}
                <span className="font-semibold text-slate-900">
                  ${detail.balanceCached.toFixed(2)}
                </span>
                {detail.stripeCustomerId ? (
                  <span className="ml-2 text-xs text-slate-500">
                    Stripe {detail.stripeCustomerId}
                  </span>
                ) : null}
              </p>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-slate-800">Pricing</h3>
              <div className="mt-2 flex flex-wrap gap-3">
                <label className="flex flex-col text-xs text-slate-600">
                  Buyer lead ($)
                  <input
                    value={buyerLead}
                    onChange={e => setBuyerLead(e.target.value)}
                    className="mt-1 w-28 rounded-md border border-slate-200 px-2 py-1.5 text-sm text-slate-900"
                  />
                </label>
                <label className="flex flex-col text-xs text-slate-600">
                  Rental lead ($)
                  <input
                    value={rentalLead}
                    onChange={e => setRentalLead(e.target.value)}
                    className="mt-1 w-28 rounded-md border border-slate-200 px-2 py-1.5 text-sm text-slate-900"
                  />
                </label>
                <button
                  type="button"
                  onClick={() => void savePricing()}
                  className={`mt-5 self-end ${accountModalAuthButtonClass}`}
                >
                  Save pricing
                </button>
              </div>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-slate-800">
                Wallet adjustment
              </h3>
              <div className="mt-2 flex flex-wrap items-end gap-3">
                <label className="flex flex-col text-xs text-slate-600">
                  Direction
                  <select
                    value={ledgerDir}
                    onChange={e =>
                      setLedgerDir(e.target.value as 'credit' | 'debit')
                    }
                    className="mt-1 rounded-md border border-slate-200 px-2 py-1.5 text-sm text-slate-900"
                  >
                    <option value="credit">Credit</option>
                    <option value="debit">Debit</option>
                  </select>
                </label>
                <label className="flex flex-col text-xs text-slate-600">
                  Amount ($)
                  <input
                    value={ledgerAmt}
                    onChange={e => setLedgerAmt(e.target.value)}
                    className="mt-1 w-28 rounded-md border border-slate-200 px-2 py-1.5 text-sm text-slate-900"
                  />
                </label>
                <label className="flex min-w-[200px] flex-1 flex-col text-xs text-slate-600">
                  Memo
                  <input
                    value={ledgerMemo}
                    onChange={e => setLedgerMemo(e.target.value)}
                    className="mt-1 rounded-md border border-slate-200 px-2 py-1.5 text-sm text-slate-900"
                  />
                </label>
                <button
                  type="button"
                  onClick={() => void submitLedger()}
                  className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-800 hover:bg-slate-50"
                >
                  Apply
                </button>
              </div>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-slate-800">
                Territory
              </h3>
              <p className="mt-1 text-xs text-slate-500">
                Center lat/lng, radius in meters (500-200000), and optional
                comma-separated FSA list.
              </p>
              <div className="mt-2 flex flex-wrap gap-3">
                <label className="flex flex-col text-xs text-slate-600">
                  Lat
                  <input
                    value={lat}
                    onChange={e => setLat(e.target.value)}
                    className="mt-1 w-28 rounded-md border border-slate-200 px-2 py-1.5 text-sm text-slate-900"
                  />
                </label>
                <label className="flex flex-col text-xs text-slate-600">
                  Lng
                  <input
                    value={lng}
                    onChange={e => setLng(e.target.value)}
                    className="mt-1 w-28 rounded-md border border-slate-200 px-2 py-1.5 text-sm text-slate-900"
                  />
                </label>
                <label className="flex flex-col text-xs text-slate-600">
                  Radius (m)
                  <input
                    value={radiusM}
                    onChange={e => setRadiusM(e.target.value)}
                    className="mt-1 w-32 rounded-md border border-slate-200 px-2 py-1.5 text-sm text-slate-900"
                  />
                </label>
                <label className="flex min-w-[220px] flex-1 flex-col text-xs text-slate-600">
                  FSA prefixes (optional)
                  <input
                    value={territoryFsas}
                    onChange={e => setTerritoryFsas(e.target.value)}
                    placeholder="M5V, M4S"
                    className="mt-1 rounded-md border border-slate-200 px-2 py-1.5 text-sm text-slate-900"
                  />
                </label>
                <button
                  type="button"
                  onClick={() => void saveTerritory()}
                  className="mt-5 self-end rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-800 hover:bg-slate-50"
                >
                  Save territory
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default AccountElAccountsPage;
